import 'react-day-picker/dist/style.css';

import Box from '@mui/material/Box';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { DeleteConfirmDialog } from '@/components/Dialog';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { edit, remove } from '@/Structures/api';
import { useStructure } from '@/Structures/useStructures';
import type { Address as AddressType } from '@/types';

import { Address as AddressComponent } from './Address';
import { Name } from './Name';

const useStyles = makeStyles((theme) => ({
  wrapper: { display: 'flex', width: '100%' },
  container: { display: 'flex', flexDirection: 'column', width: '100%' },
  header: {
    alignItems: 'baseline',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
    paddingBottom: 0,
    paddingLeft: 0,
    textAlign: 'left',
  },
}));

export const StructureShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { structureId } = useParams<{ structureId: string }>();
  if (!structureId) throw new Error('MISSING_REQUIRED_ROUTE_DATA');

  const { structure, mutate: updateStructure } = useStructure(structureId);
  const [loading, setLoading] = React.useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);

  const handleUpdateStructure =
    (fieldName: string | string[]) => async (value: string | any[] | AddressType) => {
      if (!structure) return;
      setLoading(true);
      try {
        let newStructure;
        if (typeof fieldName === 'string') {
          newStructure = { ...structure, [fieldName]: value };
        } else {
          const newFields: any = {};
          fieldName.forEach((f, i) => (newFields[f] = (value as any[])[i]));
          newStructure = { ...structure, ...newFields };
        }
        // Only update UI after successful API call
        await edit(newStructure);
        await updateStructure();
      } catch (err) {
        // Error already shown by API layer
        console.error('Failed to update structure:', err);
        throw err; // Re-throw so UI components can handle it
      } finally {
        setLoading(false);
      }
    };

  const handleDeleteConfirm = async () => {
    if (!structure) return;
    setLoading(true);
    try {
      await remove(structure.id);
      navigate('/');
      updateStructure();
      setOpenDeleteConfirm(false);
    } catch (err) {
      // Error already shown by API layer, keep dialog open
      console.error('Failed to delete structure:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <ErrorBoundary>
        <div data-component-id="STRUCTURE-VIEW" className={classes.wrapper}>
          {structure && (
            <div className={classes.container}>
              <div className={classes.header}>
                <div style={{ flexGrow: 1 }}>
                  <Name
                    name={structure.name}
                    onUpdate={handleUpdateStructure('name')}
                    onDelete={() => setOpenDeleteConfirm(true)}
                  />
                  <AddressComponent
                    address={structure.address}
                    timeZone={structure.timezone_key}
                    onUpdate={handleUpdateStructure(['address', 'timezone_key'])}
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </ErrorBoundary>

      {children}

      <DeleteConfirmDialog
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        loading={loading}
        title="Do you really want to delete this property?"
      />
    </Box>
  );
};
